import React, { useMemo } from 'react';

/**
 * ApplicationTimeline - Visual journey tracker for migration applications
 * Shows the client's progress through the application process
 */
const ApplicationTimeline = ({ currentStage, stages, application }) => {
    // Default stages if not provided
    const defaultStages = [
        { id: 'submitted', label: 'Submitted', icon: '📝', description: 'Application submitted' },
        { id: 'processing', label: 'Processing', icon: '⏳', description: 'Under review' },
        { id: 'decision', label: 'Decision', icon: '📋', description: 'Awaiting decision' },
        { id: 'offer', label: 'Offer', icon: '🎉', description: 'Offer received' },
        { id: 'visa', label: 'Visa', icon: '✈️', description: 'Visa processing' },
        { id: 'ready', label: 'Ready', icon: '🚀', description: 'Ready to travel' }
    ];

    const timelineStages = stages || defaultStages;

    const currentIndex = useMemo(() => {
        if (!currentStage) return 0;
        const idx = timelineStages.findIndex(s => s.id === currentStage);
        return idx >= 0 ? idx : 0;
    }, [currentStage, timelineStages]);

    const getStageStatus = (index) => {
        if (index < currentIndex) return 'completed';
        if (index === currentIndex) return 'current';
        return 'upcoming';
    };

    return (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h3 className="text-lg font-semibold text-slate-900">Your Journey</h3>
                    <p className="text-sm text-slate-500 mt-0.5">
                        {application?.university || 'Application'} Progress
                    </p>
                </div>
                <span className="px-3 py-1 bg-indigo-50 text-indigo-600 text-xs font-medium rounded-full">
                    Step {currentIndex + 1} of {timelineStages.length}
                </span>
            </div>

            {/* Timeline - Horizontal on desktop, vertical on mobile */}
            <div className="hidden sm:block">
                <div className="relative">
                    {/* Progress line background */}
                    <div className="absolute top-5 left-0 right-0 h-0.5 bg-slate-200 rounded-full"></div>

                    {/* Progress line filled */}
                    <div
                        className="absolute top-5 left-0 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-700 ease-out"
                        style={{ width: `${(currentIndex / (timelineStages.length - 1)) * 100}%` }}
                    ></div>

                    {/* Stage markers */}
                    <div className="relative flex justify-between">
                        {timelineStages.map((stage, idx) => {
                            const status = getStageStatus(idx);
                            return (
                                <div key={stage.id} className="flex flex-col items-center" style={{ width: `${100 / timelineStages.length}%` }}>
                                    {/* Marker */}
                                    <div className={`
                                        w-10 h-10 rounded-full flex items-center justify-center text-lg
                                        transition-all duration-300
                                        ${status === 'completed'
                                            ? 'bg-gradient-to-br from-indigo-500 to-purple-500 text-white shadow-lg shadow-indigo-200'
                                            : status === 'current'
                                                ? 'bg-white border-2 border-indigo-500 text-indigo-500 ring-4 ring-indigo-100 shadow-lg'
                                                : 'bg-slate-100 text-slate-400 border border-slate-200'
                                        }
                                    `}>
                                        {status === 'completed' ? (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        ) : (
                                            stage.icon
                                        )}
                                    </div>

                                    {/* Label */}
                                    <p className={`
                                        text-xs font-medium mt-2 text-center
                                        ${status === 'current' ? 'text-indigo-600' : status === 'completed' ? 'text-slate-700' : 'text-slate-400'}
                                    `}>
                                        {stage.label}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Mobile: Vertical timeline */}
            <div className="sm:hidden space-y-4">
                {timelineStages.map((stage, idx) => {
                    const status = getStageStatus(idx);
                    return (
                        <div key={stage.id} className="flex items-center gap-3">
                            <div className={`
                                w-8 h-8 rounded-full flex items-center justify-center text-sm flex-shrink-0
                                ${status === 'completed'
                                    ? 'bg-gradient-to-br from-indigo-500 to-purple-500 text-white'
                                    : status === 'current'
                                        ? 'bg-white border-2 border-indigo-500 text-indigo-500'
                                        : 'bg-slate-100 text-slate-400'
                                }
                            `}>
                                {status === 'completed' ? '✓' : stage.icon}
                            </div>
                            <div className="flex-1">
                                <p className={`text-sm font-medium ${status === 'current' ? 'text-indigo-600' : 'text-slate-700'}`}>
                                    {stage.label}
                                </p>
                                <p className="text-xs text-slate-500">{stage.description}</p>
                            </div>
                            {status === 'current' && (
                                <span className="text-xs bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full">Current</span>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ApplicationTimeline;
